import React from 'react';

import PropTypes from 'prop-types';

import useCioBrowseAndGqlProducts from './useCioBrowseAndGqlProducts';

const ProductContext = React.createContext();

const useProductGridData = () => {
  const context = React.useContext(ProductContext);

  if (!context) {
    throw new Error(
      'useProductGridData must be used within a ProductGridDataProvider'
    );
  }

  return context;
};

/**
 * This provider exposes all of the data and functions needed to render the grid.
 *
 * Though we could have used the useCioBrowseAndGqlProducts hook directly in the components
 * (due to the caching mechanisms of the hook), we are using this provider to prevent potential
 * missed memoization opportunities for future updates in the hook. We also want to prevent
 * potential memory leak issues for in-flight fetches in the hook while the virtualizer (virtual
 * scroll) unmounts and remounts the grid components.
 *
 * This pattern also allows us to have an easier mental model of where the data layer resides.
 * The data is retrieved and mildly transformed at the entry of the builder component. This
 * makes it easier to debug the hook as it is only called at the top level of the component tree.
 */
const ProductGridDataProvider = ({ children }) => {
  const productGridData = useCioBrowseAndGqlProducts();
  return (
    <ProductContext.Provider value={productGridData}>
      {children}
    </ProductContext.Provider>
  );
};

ProductGridDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default useProductGridData;
export { ProductGridDataProvider, ProductContext };
